import PropTypes from "prop-types";
import Icon from "@react-native-vector-icons/material-design-icons";
import theme from "assets/theme";
import moment from "moment";
import React, { useCallback, useContext, useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";
import { appIconSize } from "web/constants";
import PressableContainer from "webComponentsFormElements/PressableContainer.component";
import { AuthContext } from "webContext/Auth.context";
import { ConfirmationContext } from "webContext/Confirmation.context";
import { OrganizationContext } from "webContext/Organization.context";
import { ToastContext } from "webContext/Toast.context";

const classes = StyleSheet.create({
  informationContainer: {
    flexDirection: "row",
    gap: 15,
    alignItems: "center",
  },
  informationRow: {
    flexDirection: "row",
    alignItems: "baseline",
    gap: 2,
    alignSelf: "flex-start",
  },
  informationText: {
    color: theme.color.darkGrey,
  },
});

export default function ResourceCard(props) {
  const { resource, onPress } = props;

  const { organizationColor, deleteOrganizationResource } =
    useContext(OrganizationContext);
  const { showConfirmation } = useContext(ConfirmationContext);
  const { showToast } = useContext(ToastContext);
  const { isMessagingManager } = useContext(AuthContext);

  const stateClasses = useMemo(
    () =>
      StyleSheet.create({
        root: {
          backgroundColor: theme.color.background,
          padding: 14,
          alignItems: "center",
          justifyContent: "center",
          borderWidth: 2,
          borderColor: organizationColor,
          borderRadius: 8,
          gap: 4,
        },
        title: {
          color: organizationColor,
          fontWeight: theme.font.weight.semibold,
          fontSize: 16,
        },
      }),
    [organizationColor],
  );

  const handleDelete = useCallback(() => {
    showConfirmation({
      prompt: `Are you sure you want to permanently delete the resource ${resource.title}?`,
      onConfirm: async () => {
        try {
          await deleteOrganizationResource(resource);

          showToast(`Successfully deleted ${resource.title}.`, "success");
        } catch {
          showToast(`Failed to delete ${resource.title}.`, "error");
        }
      },
    });
  }, [deleteOrganizationResource, resource, showConfirmation, showToast]);

  const creator = resource._associations.createUser;

  return (
    <View>
    <PressableContainer style={stateClasses.root} onPress={onPress} accessibilityRole="none">
      <Text style={stateClasses.title}>{resource.title}</Text>
      <View style={classes.informationContainer}>
        <View>
          <View style={classes.informationRow}>
            <Icon name="account-circle-outline" color={theme.color.darkGrey} />
            <Text style={classes.informationText}>
              {creator.firstName} {creator.lastName}
            </Text>
          </View>
          <View style={classes.informationRow}>
            <Icon name="clock-outline" color={theme.color.darkGrey} />
            <Text style={classes.informationText}>
              {moment(resource.changeDatetime).format("MMM DD, YYYY hh:mm A")}
            </Text>
          </View>
        </View>

        {!isMessagingManager && (
          <PressableContainer testID={`delete-resource-${resource.resourceId}`} onPress={() => handleDelete()}>
            <Icon
              name="trash-can-outline"
              size={appIconSize / 2}
              color={theme.color.error}
            />
          </PressableContainer>
        )}
      </View>
    </PressableContainer>
    </View>
  );
}

ResourceCard.propTypes = {
  resource: PropTypes.object.isRequired,
  onPress: PropTypes.func,
};
